import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Tabs,
  Row,
  Col,
  Empty,
  Spin,
  Modal,
  Descriptions,
  Typography,
  Select,
  Space,
} from "antd";
import { PlusOutlined, ClearOutlined } from "@ant-design/icons";

import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import AdItem from "./Aditem";
import PositionsOverview from "./PositionsOverview";
import AdvertFormDrawer from "./AdvertFormDrawer";

import {
  fetchAllAdverts,
  fetchPositions,
  fetchAdvertStats,
  createAdvert,
  editAdvert,
  toggleAdvertStatus,
  deleteAdvert,
  restoreAdvert,
} from "../../features/advert/advertThunk";
import { clearAdvertStats } from "../../features/advert/advert";

const { Title } = Typography;

const GROUP_TABS = [
  { key: "live", label: "Live" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "inactive", label: "Paused" },
  { key: "deleted", label: "Deleted" },
];

const NON_DELETED_GROUPS = ["live", "upcoming", "past", "inactive"];

const Advert = () => {
  const dispatch = useDispatch();
  const {
    adverts,
    positions, // positions enum, for the bottom grid's filter dropdown
    listLoading,
    selectedAdvertStats,
    statsLoading,
  } = useSelector((state) => state.advert);

  const [activeGroup, setActiveGroup] = useState("live");
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // "create" | "edit"
  const [editingAd, setEditingAd] = useState(null);
  const [prefillPosition, setPrefillPosition] = useState(null);

  const [statsModalAd, setStatsModalAd] = useState(null);

  const refreshAll = () => {
    dispatch(fetchAllAdverts({ includeDeleted: "true" }));
  };

  useEffect(() => {
    dispatch(fetchPositions());
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Flat list of every non-deleted ad, for the positions table below ──
  // (that table is organized by position, not by status, so it needs
  // everything regardless of which status-group it's currently sitting in)
  const nonDeletedAds = useMemo(() => {
    if (!adverts) return [];
    return NON_DELETED_GROUPS.flatMap((g) => adverts[g] || []);
  }, [adverts]);

  // ── Position Options Formatter (for the bottom grid's filter) ──
  const positionOptions = useMemo(() => {
    if (!positions) return [];
    console.log("Positions: ", positions)
    return positions.map((pos) => {
      const val = typeof pos === "string" ? pos : pos.value || pos.key;
      const label = typeof pos === "string" ? pos : pos.label || pos.name;
      return { value: val, label };
    });
  }, [positions]);

  // ── Filter bottom grid by selected position ──
  const filteredAdverts = useMemo(() => {
    if (!adverts) return {};
    if (!selectedPosition) return adverts;

    const result = {};
    Object.keys(adverts).forEach((groupKey) => {
      result[groupKey] = (adverts[groupKey] || []).filter((ad) => {
        if (Array.isArray(ad.positions)) {
          return ad.positions.includes(selectedPosition) || ad.positions.includes("*");
        }
        return ad.position === selectedPosition || ad.position === "*";
      });
    });
    return result;
  }, [adverts, selectedPosition]);

  const currentGroupAds = filteredAdverts?.[activeGroup] || [];

  // ── Drawer open/close handlers ──

  const openCreateDrawer = (position = null) => {
    setDrawerMode("create");
    setEditingAd(null);
    setPrefillPosition(position);
    setDrawerOpen(true);
  };

  const openEditDrawer = (ad) => {
    setDrawerMode("edit");
    setEditingAd(ad);
    setPrefillPosition(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  // ── Form submit ──

  const handleFormSubmit = async (formData) => {
    const action =
      drawerMode === "create"
        ? createAdvert(formData)
        : editAdvert({ id: editingAd.id, data: formData });

    const result = await dispatch(action);
    if (!result.error) {
      setDrawerOpen(false);
      refreshAll();
    }
  };

  // ── Row actions — feed the positions table; stats/restore also used by
  // the bottom (view-only) grid ──

  const handleToggleStatus = async (ad, checked) => {
    const result = await dispatch(
      toggleAdvertStatus({ id: ad.id, status: checked ? "ACTIVE" : "INACTIVE" })
    );
    if (!result.error) refreshAll();
  };

  const handleDelete = async (ad) => {
    const result = await dispatch(deleteAdvert(ad.id));
    if (!result.error) refreshAll();
  };

  const handleRestore = async (ad) => {
    const result = await dispatch(restoreAdvert(ad.id));
    if (!result.error) refreshAll();
  };

  const handleViewStats = (ad) => {
    setStatsModalAd(ad);
    dispatch(fetchAdvertStats(ad.id));
  };

  const closeStatsModal = () => {
    setStatsModalAd(null);
    dispatch(clearAdvertStats());
  };

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />

        <div className="listContainer">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <Title level={3} style={{ margin: 0 }}>
              Ads Management
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreateDrawer(null)}
            >
              Create New Ad
            </Button>
          </div>

          {/* ── Positions table — the management surface. One section per
              position, "+ Add Ad" per position, inline edit / status
              toggle / delete per row. ── */}
          <PositionsOverview
            ads={nonDeletedAds}
            onAddForPosition={openCreateDrawer}
            onEdit={openEditDrawer}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
          />

          {/* ── Status-grouped grid — browsing only. No edit / toggle /
              delete here; that's the table above. Stats + restore-from-
              deleted still live here. ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 16,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Tabs
              activeKey={activeGroup}
              onChange={setActiveGroup}
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              items={GROUP_TABS.map(({ key, label }) => ({
                key,
                label: `${label} (${filteredAdverts?.[key]?.length || 0})`,
              }))}
            />

            <Space style={{ marginBottom: 16 }}>
              <Select
                placeholder="Filter by Position"
                allowClear
                value={selectedPosition}
                onChange={(value) => setSelectedPosition(value)}
                options={positionOptions}
                style={{ width: 220 }}
              />

              {selectedPosition && (
                <Button
                  icon={<ClearOutlined />}
                  onClick={() => setSelectedPosition(null)}
                >
                  Clear Filter
                </Button>
              )}
            </Space>
          </div>

          {listLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : currentGroupAds.length === 0 ? (
            <Empty
              description={
                selectedPosition
                  ? `No ads found for position "${selectedPosition}" in ${activeGroup}`
                  : "No ads in this group"
              }
              style={{ marginTop: 40 }}
            >
              {selectedPosition && (
                <Button type="link" onClick={() => setSelectedPosition(null)}>
                  Clear position filter
                </Button>
              )}
            </Empty>
          ) : (
            <Row gutter={[16, 16]}>
              {currentGroupAds.map((ad) => (
                <Col xs={24} sm={12} md={8} lg={6} key={ad.id}>
                  <AdItem
                    ad={ad}
                    group={activeGroup}
                    onRestore={() => handleRestore(ad)}
                    onViewStats={() => handleViewStats(ad)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>

        <AdvertFormDrawer
          open={drawerOpen}
          mode={drawerMode}
          initialAd={editingAd}
          prefillPosition={prefillPosition}
          onClose={closeDrawer}
          onSubmit={handleFormSubmit}
        />

        <Modal
          title={statsModalAd ? `Stats — ${statsModalAd.name}` : "Stats"}
          open={!!statsModalAd}
          onCancel={closeStatsModal}
          footer={null}
        >
          {statsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin />
            </div>
          ) : (
            selectedAdvertStats && (
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Impressions">
                  {selectedAdvertStats.impressions}
                </Descriptions.Item>
                <Descriptions.Item label="Clicks">
                  {selectedAdvertStats.clicks}
                </Descriptions.Item>
                <Descriptions.Item label="CTR">
                  {selectedAdvertStats.ctr}
                </Descriptions.Item>
              </Descriptions>
            )
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Advert;
